import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { setLanguage } from "@/i18n/vueI18n";
import { sendLanguageToFlutter } from "@/helpers/flutter/features/language";
import type { Language } from "@/types/language";

/**
 * Available languages in the application.
 * This should match the languages array in vueI18n.js
 */
const AVAILABLE_LANGUAGES: Language[] = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱" },
];

/**
 * Notifies the Flutter wrapper of the active language.
 */
export function syncLanguageToFlutter(language?: string): void {
  sendLanguageToFlutter(language);
}

/**
 * Composable for managing language selection and display.
 * Uses the setLanguage function from vueI18n.js which handles:
 * - Persisting to localStorage
 * - Syncing to user profile via Pinia
 * - Logging analytics events
 * - Syncing to Flutter wrapper
 */
export function useLanguages() {
  const { locale } = useI18n();

  const availableLanguages = computed(() => AVAILABLE_LANGUAGES);
  const currentLanguageCode = computed(() => locale.value);

  const setLanguageWithFlutter = (key: string) => {
    setLanguage(key);
    sendLanguageToFlutter(key);
  };

  return {
    availableLanguages,
    currentLanguageCode,
    setLanguage: setLanguageWithFlutter,
    syncLanguageToFlutter,
  };
}
